import { datadogRum } from '@datadog/browser-rum'
import { camelToSnake } from '@owner/casing'
import { UnleashClient } from 'unleash-proxy-client'
import { type Plugin } from 'vue'

export const UNLEASH_URL =
  'https://us.app.unleash-hosted.com/usgg0011/api/frontend' as const

export const FeatureFlags = new UnleashClient({
  appName: 'owner-hermes',
  clientKey: import.meta.env.VITE_UNLEASH_KEY,
  environment:
    import.meta.env.MODE === 'production' ? 'production' : 'development',
  url: UNLEASH_URL,
})

const convertUnleashToENVName = (str: string): string =>
  `VITE_FF_${camelToSnake(str)
    .replace(/\./g, '_')
    .replace(/-/g, '_')
    .toUpperCase()}`

// Extend the isEnabled method to be able to handle FF set up in ENV variables
// This is useful so engineers can locally override whatever value is in Unleash
// The ENV variables format for FF is the following:
// If in Unleash we wrote a `.` in the ENV variable we will write a `_`
// If in Unleash we wrote a `-` in the ENV variable we will write a `_`
// If in Unleash we wrote in camelCase, in the ENV variable we will write SNAKE_CASE
// If in Unleash we wrote in lowerCase, in the ENV variable we will write in upper case
// We will prefix our ENV FF names with `VITE_FF_`
// Examples:
// Unleash: `hermes.customerImportRedesign being enabled` -> ENV: `VITE_FF_HERMES_CUSTOMER_IMPORT_REDESIGN=true`
// Unleash: `hermes.hot-launch-tool being disabled` -> ENV: `VITE_FF_HERMES_HOT_LAUNCH_TOOL=false`
FeatureFlags.isEnabled = (function (originalIsEnabled) {
  return function (featureFlag) {
    const environment = import.meta.env.MODE
    const isStorybook = Boolean(import.meta.env.STORYBOOK)

    // In Storybook, never use env override so MSW (e.g. default empty toggles) controls flags
    if (environment === 'development' && !isStorybook) {
      // E2E test overrides injected via Playwright addInitScript
      const e2eOverrides = (window as unknown as Record<string, unknown>)
        .__unleash_e2e_overrides__ as
        | Record<string, boolean | string>
        | undefined
      if (e2eOverrides && featureFlag in e2eOverrides) {
        return Boolean(e2eOverrides[featureFlag])
      }

      const ffName = convertUnleashToENVName(featureFlag)

      const envFlag = import.meta.env[ffName]

      if (typeof envFlag !== 'undefined') {
        return envFlag === 'true'
      }
    }

    const result = originalIsEnabled.call(this, featureFlag)

    // Add feature flag values upon evaluation so that we don't overload the RUM
    // events with superfluous flags. Also, Datadog collapses flags into objects
    // if we use the dot-separated flag names provided by Unleash, so we replace
    // those with "_" for readability.
    datadogRum.addFeatureFlagEvaluation(featureFlag.replace(/\./g, '_'), result)

    return result
  }
  // eslint-disable-next-line @typescript-eslint/unbound-method
})(FeatureFlags.isEnabled)

const FeatureFlagsPlugin: Plugin = {
  async install(app) {
    await FeatureFlags.start()
    app.config.globalProperties.$featureFlags = FeatureFlags
  },
}

export default FeatureFlagsPlugin
